// Global imports
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  formatDate,
  checkNumberOfDays,
  checkDateInPast,
  checkDayIsSaturday,
  checkDayIsSunday,
  checkValidStartDate,
  checkValidEndDate,
} from '~/utils'

/**
 * Minimal error shape surfaced by `useProductDates` via `dateError`.
 *
 * The hook accepts this same shape as its external `pricesError`
 * prop, which means any upstream error state with `{ id, message }`
 * (including `BrendaChargesErrorState` from `useBrendaCharges`) can
 * be passed straight through without adaptation.
 */
export interface DateError {
  id: number
  message: string
}

export type DateChangeArgs = {
  value: string
  clearProductData?: () => void
  resetQuantity?: () => void
}

interface UseProductDatesParams {
  buyProduct?: boolean
  allowSaturday?: boolean
  averageHire?: number | null
  pricesError?: DateError | null
}

/**
 * useProductDates
 *
 * Shared date state, validation, and change handlers used by both
 * the PDP (useProductData) and the basket dates modal (useBasketDates).
 *
 * Responsibilities:
 * 1) Compute sensible default dates (bumped off weekends)
 * 2) Own startDate / endDate / daysDifference state
 * 3) Validate dates on every change (past, Saturday, Sunday, range)
 * 4) Surface external pricing errors through dateError
 * 5) Provide guarded change handlers that reject invalid selections
 */
const useProductDates = ({
  buyProduct = false,
  allowSaturday = false,
  averageHire,
  pricesError,
}: UseProductDatesParams = {}) => {
  const errorIdRef = useRef(0)

  // ----------------------------
  // Default dates (memoised)
  // ----------------------------
  // Bump dates off blocked days (Sunday always, Saturday optionally)
  // Set end date based off averageHire for hire products

  const defaults = useMemo(() => {
    const bumpOffBlockedDay = (d: Date) => {
      while (d.getDay() === 0 || (!allowSaturday && d.getDay() === 6)) {
        d.setDate(d.getDate() + 1)
      }
      return d
    }

    // Start date: today, bumped to next valid day
    const today = bumpOffBlockedDay(new Date())
    const start = formatDate(today, 'input')

    // Buy products: start and end are always the same day
    if (buyProduct) {
      return { minDate: start, startDate: start, endDate: start }
    }

    // Hire products: prefer averageHire; fallback to 7 days. Brenda counts
    // the start and end day both, so a 7 day hire ends 6 days after the start
    const durationDays = averageHire && averageHire > 0 ? averageHire : 7
    const end = new Date(today)
    end.setDate(end.getDate() + durationDays - 1)
    bumpOffBlockedDay(end)

    return {
      minDate: start,
      startDate: start,
      endDate: formatDate(end, 'input'),
    }
  }, [buyProduct, averageHire, allowSaturday])

  // ----------------------------
  // Date state
  // ----------------------------

  const [startDate, setStartDate] = useState<string>(defaults.startDate)
  const [endDate, setEndDate] = useState<string>(defaults.endDate)
  const [daysDifference, setDaysDifference] = useState<number>(
    checkNumberOfDays(defaults.startDate, defaults.endDate)
  )
  const [dateError, setDateError] = useState<DateError | null>(null)
  const hasDateError = dateError !== null

  // ----------------------------
  // Date validation effect
  // ----------------------------

  useEffect(() => {
    if (!startDate || !endDate) {
      setDateError(null)
      return
    }

    // Update day count
    const nextDays = checkNumberOfDays(startDate, endDate)
    setDaysDifference(nextDays)

    let nextError: string | null = null

    if (!buyProduct && nextDays <= 0) {
      nextError = 'End date must be after start date'
    } else if (checkDateInPast(startDate) || checkDateInPast(endDate)) {
      nextError = 'Date selections cannot be in the past'
    } else if (
      !allowSaturday &&
      (checkDayIsSaturday(startDate) || checkDayIsSaturday(endDate))
    ) {
      nextError =
        'Date selections cannot start or end on a Saturday for this product'
    } else if (checkDayIsSunday(startDate) || checkDayIsSunday(endDate)) {
      nextError = 'Date selections cannot start or end on a Sunday'
    }

    // Prevent repeated IDs for same message
    setDateError(prev => {
      if (!nextError) return null
      if (prev?.message === nextError) return prev

      errorIdRef.current += 1

      return {
        id: errorIdRef.current,
        message: nextError,
      }
    })
  }, [startDate, endDate, buyProduct, allowSaturday])

  // ----------------------------
  // Surface external pricing errors
  // ----------------------------

  useEffect(() => {
    if (pricesError) setDateError(pricesError)
  }, [pricesError])

  // ----------------------------
  // Date change handlers
  // ----------------------------
  // Centralise all date validation + side-effects.
  // Components pass the selected value and optional callbacks.

  const handleStartDateChange = useCallback(
    ({ value, clearProductData, resetQuantity }: DateChangeArgs) => {
      if (checkDateInPast(value)) return
      if (!allowSaturday && checkDayIsSaturday(value)) return
      if (checkDayIsSunday(value)) return

      // Reset dependent state
      resetQuantity?.()
      clearProductData?.()

      // Clamp endDate to startDate if start moves past end
      if (!checkValidStartDate(value, endDate)) {
        setEndDate(value)
      }

      setStartDate(value)
    },
    [allowSaturday, endDate]
  )

  const handleEndDateChange = useCallback(
    ({ value, clearProductData, resetQuantity }: DateChangeArgs) => {
      if (checkDateInPast(value)) return
      if (!allowSaturday && checkDayIsSaturday(value)) return
      if (checkDayIsSunday(value)) return

      // Reset dependent state
      resetQuantity?.()
      clearProductData?.()

      // Keep date range valid
      if (!checkValidEndDate(startDate, value)) {
        setStartDate(value)
      }

      setEndDate(value)
    },
    [allowSaturday, startDate]
  )

  // Handle a range selection from DateRangePicker (both dates at once)
  const handleRangeSelect = useCallback(
    (start: string, end: string) => {
      setStartDate(start)
      setEndDate(end)
    },
    []
  )

  const handleBuyDeliveryDateChange = useCallback(
    ({ value, clearProductData, resetQuantity }: DateChangeArgs) => {
      if (checkDateInPast(value)) return
      if (!allowSaturday && checkDayIsSaturday(value)) return
      if (checkDayIsSunday(value)) return

      resetQuantity?.()
      clearProductData?.()

      setStartDate(value)
      setEndDate(value)
    },
    [allowSaturday]
  )

  return {
    // State
    startDate,
    endDate,
    daysDifference,
    dateError,
    hasDateError,
    minDate: defaults.minDate,

    // Setters (for external control like cart modal pre-fill)
    setStartDate,
    setEndDate,
    setDateError,

    // Handlers
    handleRangeSelect,
    handleStartDateChange,
    handleEndDateChange,
    handleBuyDeliveryDateChange,
  }
}

export default useProductDates
